import { CommentStatus, type Comment as CommentType } from '@/@types/comment'
import { formatAsRelativeTime } from '@/utils/day'
import { type GetCommentParams, getCommentQueryOptions } from '@/utils/fetchers/comment'
import usePost from '@/utils/hooks/usePost'
import { css } from '@emotion/react'
import { Loading } from '@ruguoapp/jike-fe-components'
import { StickStickIcon } from '@ruguoapp/jike-fe-icons'
import { useSuspenseQuery } from '@tanstack/react-query'
import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react'
import { Suspense } from 'react'
import AuthorTag from '../AuthorTag'
import ClickableStackAvatarGroup from '../ClickableStackAvatarGroup'
import ContentContainer from '../ContentContainer'
import PictureGrid from '../PictureGrid'
import ScreenName from '../ScreenName'
import TextContent from '../TextContent'
import CommentActions from './CommentActions'
import CommentHeader from './CommentHeader'
import CommentReplies from './CommentReplies'
import { useToCommentPage } from './hooks'

export type CommentData = GetCommentParams
export interface CommentProps extends HTMLMotionProps<'div'> {
	/**
	 * comment id
	 */
	data: CommentData

	/**
	 * 是否显示操作按钮
	 *
	 * @default false
	 */
	noActions?: boolean

	/**
	 * 是否不显示标题后缀
	 *
	 * @default false
	 */
	noSuffixTitle?: boolean

	/**
	 * 是否显示回复引导线
	 *
	 * @default false
	 */
	showThreadLine?: boolean

	/**
	 * @default false
	 */
	noReplies?: boolean
}

export default function Comment({
	data: commentQueryParams,
	noActions = false,
	showThreadLine = false,
	noSuffixTitle = false,
	noReplies = false,
	...props
}: CommentProps) {
	const { data } = useSuspenseQuery<CommentType>(getCommentQueryOptions(commentQueryParams))
	const {
		user,
		createdAt,
		content,
		threadId,
		targetId,
		targetType,
		replyToComment,
		likeCount,
		replyCount,
		level,
		isPinned,
	} = data

	const targetPost = usePost(targetId)
	const targetPostUserName = targetPost?.user?.username
	const isPostAuthor = targetPostUserName === user.username

	const hasReply = !!(!noSuffixTitle && replyToComment)
	const { canToCommentPage, toCommentPage } = useToCommentPage(data)

	const isDeleted = data.deleted || data.status === CommentStatus.DELETED
	return (
		<AnimatePresence mode="popLayout">
			<motion.div
				exit={{
					opacity: 0,
				}}
				data-clickable-feedback={canToCommentPage}
				onClick={toCommentPage}
				{...props}
			>
				{isPinned && (
					<div
						css={css`
							color: var(--tint-error);
							display: flex;
							align-items: center;
							gap: 4px;
							font-size: 14px;
							font-weight: 500;
							margin-bottom: 8px;
						`}
					>
						<StickStickIcon
							css={css`
								width: 16px;
								height: 16px;
							`}
							color="currentColor"
						/>
						置顶
					</div>
				)}
				<div
					css={css`
						display: grid;
						grid-template-columns: auto 1fr;
						grid-gap: 10px;
					`}
				>
					<div
						css={css`
							display: flex;
							flex-direction: column;
						`}
					>
						<ClickableStackAvatarGroup data={[data.user]} />

						{showThreadLine && (
							<div
								css={css`
									flex: 1;
									display: flex;
									justify-content: center;
								`}
							>
								<div
									css={css`
										width: 1px;
										height: 100%;
										background-color: var(--tint-separator);
										border-radius: 2px;
										transform: translateY(10px);
									`}
								/>
							</div>
						)}
					</div>

					<div>
						<CommentHeader
							title={
								<div
									css={css`
										display: flex;
										align-items: center;
										justify-content: space-between;
										gap: 4px;
										height: 20px;
										text-wrap: nowrap;
									`}
								>
									<div
										css={css`
											display: grid;
											align-items: center;
											grid-template-columns: auto ${isPostAuthor ? 'auto' : ''} ${hasReply
													? 'auto'
													: ''};
											gap: 4px;
											overflow: hidden;
											text-overflow: ellipsis;
											white-space: nowrap;

											> * {
												overflow: hidden;
											}
										`}
									>
										<ScreenName
											data={data.user}
											css={css`
												> a {
													overflow: hidden;
													text-overflow: ellipsis;
												}
											`}
										/>
										{isPostAuthor && <AuthorTag />}
										{hasReply && (
											<div
												css={css`
													color: var(--tint-secondary);
													display: grid;
													grid-template-columns: auto 1fr;
													gap: 2px;

													> * {
														overflow: hidden;
													}
												`}
											>
												<span
													css={css`
														font-weight: 400;
													`}
												>
													回复
												</span>
												<ScreenName
													data={replyToComment.user}
													css={css`
														> a {
															overflow: hidden;
															text-overflow: ellipsis;
														}
													`}
												/>
											</div>
										)}
									</div>
									{!noActions && <CommentActions data={data} />}
								</div>
							}
							desc={formatAsRelativeTime(createdAt)}
						/>

						<div
							css={css`
								margin-top: 6px;
							`}
						>
							<ContentContainer isDeleted={isDeleted} css={css``}>
								<TextContent content={data.content} urlsInText={data.urlsInText} />
								{data.pictures && (
									<PictureGrid
										data={data.pictures}
										css={css`
											margin-top: 6px;
										`}
									/>
								)}
							</ContentContainer>
						</div>

						<Suspense
							fallback={
								<motion.div
									initial={{
										opacity: 0,
									}}
									animate={{
										opacity: 1,
									}}
									exit={{
										opacity: 0,
									}}
									css={css`
										height: 30px;
										display: flex;
										justify-content: center;
									`}
								>
									<Loading />
								</motion.div>
							}
						>
							{!noReplies && !noActions && <CommentReplies data={data} />}
						</Suspense>
					</div>
				</div>
			</motion.div>
		</AnimatePresence>
	)
}
